<template>
  <BittsLoading :is-loading="!allReady">
    <div class="max-h-full h-full">
      <BittsBreadcrumbs
        v-if="!isOnListsPage"
        :crumbs="breadcrumbs"
        :active-crumb-index="1"
        :last-crumb-index="1"
        class="mb-24"
        data-testid="breadcrumb-link"
        @root-clicked="onBreadcrumbClicked"
      />
      <BillingCTA
        v-if="isFreeTier"
        message="Do more with Lists"
        description="Upgrade to save, share, and collaborate with partners"
        :billing-interaction="{
          cta: LISTS_CTAS.CREATE_LIST,
          event_site: EVENT_SITES.LIST_BANNER,
        }"
        class="mb-24"
      />
      <div
        v-if="
          !!combinedListsByOwner.yourLists.length ||
          !!combinedListsByOwner.sharedWithYou.length ||
          !!folders.length ||
          !isOnListsPage
        "
        class="lists-table flex flex-col gap-12"
      >
        <BittsQuickFilters
          :filter-options="filterOptions"
          :filter-value-options="filterValueOptions"
          :disabled="freeWithNoLists && !isUpsellExemptTab"
          filter-select-position="right"
          class="flex items-center mb-0 gap-0"
          @update-filters="handleUpdateFilters"
        >
          <template #prefix-controls>
            <BittsSegment
              v-if="isOnListsPage && !hasSalesOnlyRole"
              :tabs="tabs"
              :active-index="activeTab"
              :disabled="
                freeWithNoLists &&
                !isUpsellExemptTab &&
                !hasExternallySharedLists
              "
              class="self-start"
              @change:segment="(tab) => (activeTab = tab)"
            />
            <div
              v-else-if="!hasSalesOnlyRole"
              data-testid="folder-name"
              class="font-bold text-xl flex items-center gap-12"
            >
              {{ folder?.name }}
              <BittsDropdown
                ref="dropdownRef"
                :disabled="isFreeTier"
                placement="bottomLeft"
                @visibility-changed="onDropdownVisibleChange"
              >
                <template #prompt>
                  <BittsFilterButton
                    data-testid="edit-folder-button"
                    :prefix-icon="['fak', 'edit']"
                    :show-icon-only="true"
                    :is-open="isDropdownVisible"
                    :disabled="isFreeTier"
                  />
                </template>
                <template #content>
                  <div class="px-4">
                    <button
                      v-for="option in options"
                      type="button"
                      :key="option.label"
                      class="flex items-center gap-8 text-left w-full py-4 px-8 hover:bg-neutral-background-weak cursor-pointer rounded-4"
                      @click="option.onClick"
                    >
                      <FontAwesomeIcon
                        :icon="option.icon"
                        :class="
                          option.label === 'Delete folder'
                            ? 'text-danger-accent'
                            : 'text-neutral-accent'
                        "
                      />
                      <div
                        :class="
                          option.label === 'Delete folder'
                            ? 'text-danger-text'
                            : 'text-neutral-text-strong'
                        "
                      >
                        {{ option.label }}
                      </div>
                    </button>
                  </div>
                </template>
              </BittsDropdown>
            </div>
          </template>
          <template #controls>
            <div class="lists-table__controls">
              <MoveReportsDropdown
                v-if="
                  !!checkedLists.length && hasWritePermissions && !isFreeTier
                "
                :checked-reports="checkedLists"
                :move-to-unfiled="!isOnListsPage"
                @show-new-folder-modal="
                  emit('show-create-folder-modal', checkedLists)
                "
                @reports-moved="onReportsMoved"
                @delete-cancelled="onReportsMoved"
              />
              <BittsInput
                v-model="searchQuery"
                type="search"
                name="search"
                placeholder="Search lists"
                size="small"
                :disabled="freeWithNoLists && !isUpsellExemptTab"
                class="max-w-[176px]"
              />
            </div>
          </template>
        </BittsQuickFilters>
        <div
          class="relative"
          role="tabpanel"
          :aria-label="activeSegmentTitle"
          :style="
            showPartialOverlay
              ? { '--locked-rows-extra-padding': `${OVERLAY_MIN_HEIGHT}px` }
              : undefined
          "
        >
          <UpsellOverlay
            v-if="isFreeTier && !isUpsellExemptTab && !hasLookalikeReport"
            title="Don't lose your Lists"
            text="Upgrade to get access to your saved Lists"
            :billing-interaction="{
              cta: LISTS_CTAS.CREATE_LIST,
              event_site: EVENT_SITES.LIST_TABLE_UPSELL_OVERLAY,
            }"
            class="rounded-b-bts-lg"
          />
          <UpsellOverlay
            v-if="showPartialOverlay"
            title="Don't lose your Lists"
            text="Upgrade to get access to your saved Lists"
            :billing-interaction="{
              cta: LISTS_CTAS.CREATE_LIST,
              event_site: EVENT_SITES.LIST_TABLE_UPSELL_OVERLAY,
            }"
            class="lists-table__locked-rows-overlay rounded-b-bts-lg"
            :style="{
              top: `${lockedRowsTop}px`,
              height: `${overlayHeight}px`,
            }"
          />
          <BittsTablePro
            ref="tableRef"
            :columns="visibleColumns"
            :rows="rows"
            :use-search="false"
            :row-height="LIST_ROW_HEIGHT"
            :suppress-movable-columns="true"
            :no-rows="!rows.length"
            :pagination-page-size="isFreeTier ? 10 : 20"
            :no-rows-props="{
              title: 'No Lists found',
              description: !!listsForTable.length
                ? 'Try a different search term or filter to find Lists'
                : 'Add Lists to this folder to get started',
              icon: ['fak', 'search'],
            }"
            :row-selection="{
              mode: 'multiRow',
              enableClickSelection: false,
              checkboxes: hasWritePermissions && !isFreeTier,
              headerCheckbox: hasWritePermissions && !isFreeTier,
              selectAll: 'currentPage',
              isRowSelectable: (row) =>
                !row.data?.isStaticList &&
                !row.data?.isFolder &&
                !isOnboardingDemoReport(row.data?.report),
            }"
            :on-row-selected="
              (e: RowSelectedEvent<ReportCell>) => onListChecked(e)
            "
            :zebra="true"
            @row-clicked="onRowClicked"
          />
        </div>
      </div>
      <BittsEmptyState
        v-else
        :title="
          hasSalesOnlyRole ? 'Waiting on your first List' : 'No Lists yet'
        "
        class="h-full"
        svg-name="emptyStateReports"
      >
        <template #subtitle>
          <template v-if="hasSalesOnlyRole">
            <p class="text-neutral-text mb-24">
              You'll see Lists here once someone on your team shares one with
              you. If you're expecting access, try pinging a teammate to share
              their List.
            </p>
            <p class="text-neutral-text-strong font-bold mb-8">Or jump into:</p>
            <ul class="text-neutral-text text-left">
              <li>
                <BittsLink
                  :show-arrow="true"
                  text="Deal Navigator to work active opportunities"
                  url="deal-navigator-close-deals"
                />
              </li>
              <li>
                <BittsLink
                  :show-arrow="true"
                  text="Pipeline Generation to uncover new pipeline"
                  url="deal-navigator-generate-pipeline"
                />
              </li>
            </ul>
          </template>
          <template v-else>
            <p class="text-neutral-text mb-24">
              Create your first List to account map with a partner and uncover
              overlaps
            </p>
            <BittsButton text="Create a List" @click="emit('create-list')" />
          </template>
        </template>
      </BittsEmptyState>
    </div>
  </BittsLoading>
</template>

<script setup lang="ts">
import {
  BittsBreadcrumbs,
  BittsButton,
  BittsDropdown,
  BittsEmptyState,
  BittsFilterButton,
  BittsInput,
  BittsLink,
  BittsLoading,
  BittsQuickFilters,
  BittsSegment,
  BittsTablePro,
} from '@crossbeam/bitts';
import { FilterProp } from '@crossbeam/bitts/src/BittsQuickFilter/types';
import { EVENT_SITES, LISTS_CTAS } from '@crossbeam/itly/src/analytics';

import { RowSelectedEvent } from '@ag-grid-community/core';
import { refDebounced } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';

import BillingCTA from '@/components/billing/BillingCTA.vue';
import UpsellOverlay from '@/components/billing/UpsellOverlay.vue';
import {
  isOnboardingDemoReport,
  useIsOnboardingDemoEnabled,
} from '@/components/onboarding/onboardingDemoReport';
import MoveReportsDropdown from '@/components/reports/MoveReportsDropdown.vue';

import { useCombinedLists } from '@/composables/reports/useCombinedLists';
import useAuth from '@/composables/useAuth';
import useRecordExportLimits from '@/composables/useRecordExportLimits';
import { useReportsColumns } from '@/composables/useReportsColumns';
import { CREATOR, NO_ACCESS } from '@/constants/shared_list';
import { useBillingStore } from '@/stores';
import { useRootStore } from '@/stores/RootStore';
import { ReportCell, ReportFolder, TrimmedReport } from '@/types/reports';
import { SharedList } from '@/types/shared_lists';

const {
  hasWritePermissions = false,
  trialOver = false,
  checkedLists = [],
  folderId = null,
  partnerId = null,
} = defineProps<{
  hasWritePermissions?: boolean;
  trialOver?: boolean;
  checkedLists?: string[];
  folderId?: string | null;
  partnerId?: number | null;
}>();
const emit = defineEmits<{
  (
    e: 'show-create-folder-modal' | 'update:checkedLists',
    checkedLists: string[],
  ): void;
  (e: 'edit-folder' | 'delete-folder' | 'create-list'): void;
}>();

const router = useRouter();
const { isSupernodeOrEnterpriseTier } = storeToRefs(useBillingStore());
const { hasSalesOnlyRole } = storeToRefs(useRootStore());
const { hasScope, currentOrg } = useAuth();
const { exportLimitReached } = useRecordExportLimits();

const isOnListsPage = computed(() => !folderId);
const isOnboardingDemoEnabled = useIsOnboardingDemoEnabled();

const dropdownRef = ref<InstanceType<typeof BittsDropdown> | null>(null);
const options = computed(() => [
  {
    label: 'Edit name',
    icon: ['fak', 'edit'],
    onClick: () => {
      if (dropdownRef.value) dropdownRef.value.isVisible = false;
      emit('edit-folder');
    },
  },
  {
    label: 'Delete folder',
    icon: ['fak', 'delete'],
    onClick: () => {
      if (dropdownRef.value) dropdownRef.value.isVisible = false;
      emit('delete-folder');
    },
  },
]);
const {
  combinedListsByOwner,
  hasExternallySharedLists,
  reportsByOwner,
  folders,
  allReady,
  reportsStore,
  getCellInfo,
  folder,
  partnerOrgs,
  listAccess,
} = useCombinedLists(folderId);

const breadcrumbs = computed(() => [
  { name: 'all-lists', text: 'Lists' },
  { name: 'folder', text: folder.value?.name ?? '' },
]);

function onBreadcrumbClicked() {
  router.push('/lists');
}

const freeWithNoLists = computed(
  () => isFreeTier.value && listAccess.value === NO_ACCESS,
);
onMounted(reportsStore.refreshReportsStore);

const filterOptions = computed<FilterProp[]>(() => {
  const options: FilterProp[] = [];
  // Hide partner filter when partnerId prop is set (already filtered)
  if (!partnerId) {
    options.push({
      label: 'Partner Overlaps',
      faIcon: ['fak', 'partner'],
      datatype: 'text',
      value: 'partners_in_list',
      useSearch: true,
    });
  }
  options.push({
    label: 'Owner',
    faIcon: ['fak', 'person'],
    datatype: 'text',
    value: 'owner',
    useSearch: true,
  });
  return options;
});
const selectedPartnerIds = ref<number[]>([]);
const selectedOwnerIds = ref<number[]>([]);

const isDropdownVisible = ref(false);

function onDropdownVisibleChange(visible: boolean) {
  isDropdownVisible.value = visible;
}
function handleUpdateFilters(filterInfo: {
  filterValues: Record<
    string,
    (string | number)[] | Record<string, unknown> | null
  >;
}) {
  const selectedPartners = filterInfo.filterValues.partners_in_list;
  if (Array.isArray(selectedPartners)) {
    selectedPartnerIds.value = selectedPartners.map((id) =>
      typeof id === 'number' ? id : parseInt(id as string, 10),
    );
  } else {
    selectedPartnerIds.value = [];
  }

  const selectedOwners = filterInfo.filterValues.owner;
  if (Array.isArray(selectedOwners)) {
    selectedOwnerIds.value = selectedOwners.map((id) =>
      typeof id === 'number' ? id : parseInt(id as string, 10),
    );
  } else {
    selectedOwnerIds.value = [];
  }
}

const { columns, hasPartialAccess, isFreeTier } = useReportsColumns();

const visibleColumns = computed(() => {
  if (activeTab.value === TAB_FOLDERS) {
    return columns.value
      .filter((col) => col.field === 'reportName' || col.field === 'lastEdited')
      .map((col) =>
        col.field === 'reportName'
          ? { ...col, headerName: 'Folder Name' }
          : col,
      );
  }
  return columns.value;
});

const searchQuery = ref('');
const debouncedSearchQuery = refDebounced(searchQuery, 300);

function searchMatchesAnyPartner(query: string): boolean {
  if (!query) return false;
  return partnerOrgs.value.some((org) =>
    org.name.toLowerCase().includes(query),
  );
}

const tableRef = ref<InstanceType<typeof BittsTablePro> | null>(null);

function onListChecked(event: RowSelectedEvent<ReportCell>) {
  const selectedNodes = event.api.getSelectedNodes();
  const selectedIds = selectedNodes
    .map((n) => n.data?.report?.id)
    .filter((id): id is string => !!id);
  emit('update:checkedLists', selectedIds);
}

function clearSelection() {
  tableRef.value?.gridApi?.deselectAll();
}

function onReportsMoved() {
  emit('update:checkedLists', []);
  clearSelection();
}

function onRowClicked(rowData: ReportCell) {
  // Don't navigate if row is disabled
  if (rowData.trialOver || rowData.isDisabledByTier) return;
  // Navigate to the report/folder link
  if (rowData.reportLink) {
    router.push(rowData.reportLink);
  }
}

const hideExport = computed(
  () =>
    !isSupernodeOrEnterpriseTier.value ||
    !hasScope('write:report-exports') ||
    exportLimitReached.value,
);

const TAB_ALL = 0;
const TAB_YOUR_LISTS = 1;
const TAB_SHARED_WITH_YOU = 2;
const TAB_FOLDERS = 3;

const activeTab = ref<
  | typeof TAB_ALL
  | typeof TAB_YOUR_LISTS
  | typeof TAB_SHARED_WITH_YOU
  | typeof TAB_FOLDERS
>(TAB_ALL);
const isUpsellExemptTab = computed(
  () =>
    activeTab.value === TAB_SHARED_WITH_YOU || activeTab.value === TAB_FOLDERS,
);

const hasLookalikeReport = computed(() =>
  rows.value.some((r) => isOnboardingDemoReport(r.report)),
);

const showPartialOverlay = computed(
  () =>
    isFreeTier.value &&
    ((isUpsellExemptTab.value && rows.value.some((r) => r.isDisabledByTier)) ||
      hasLookalikeReport.value),
);

const AG_HEADER_HEIGHT = 32;
const AG_BODY_PADDING_TOP = 16;
const LIST_ROW_HEIGHT = 60;
const FREE_TIER_PAGE_SIZE = 10;
const OVERLAY_MIN_HEIGHT = 300;
const enabledRowsCount = computed(
  () => rows.value.filter((r) => !r.isDisabledByTier).length,
);
const lockedRowsTop = computed(() => {
  return (
    AG_HEADER_HEIGHT +
    AG_BODY_PADDING_TOP +
    Math.min(enabledRowsCount.value, FREE_TIER_PAGE_SIZE) * LIST_ROW_HEIGHT
  );
});
const overlayHeight = computed(() => {
  const visibleEnabled = Math.min(enabledRowsCount.value, FREE_TIER_PAGE_SIZE);
  const visibleTotal = Math.min(rows.value.length, FREE_TIER_PAGE_SIZE);
  return OVERLAY_MIN_HEIGHT + (visibleTotal - visibleEnabled) * LIST_ROW_HEIGHT;
});

watch(
  [allReady, isFreeTier],
  ([ready, freeTier]) => {
    if (!ready || !freeTier) return;
    if (hasExternallySharedLists.value) {
      activeTab.value = TAB_SHARED_WITH_YOU;
    }
  },
  { immediate: true },
);
const tabs = computed(() => {
  const listsSource = combinedListsByOwner.value;
  const yourListsDisabled = !listsSource.yourLists.length;
  const sharedWithYouDisabled = !listsSource.sharedWithYou.length;
  const baseTabs = [
    {
      title: 'All',
      id: TAB_ALL,
      count: listsSource.yourLists.length + listsSource.sharedWithYou.length,
    },
    {
      title: 'Your Lists',
      id: TAB_YOUR_LISTS,
      disabled: yourListsDisabled,
      count: listsSource.yourLists.length,
      tooltipProps: yourListsDisabled
        ? { tooltipTitle: 'Lists that you create will appear here' }
        : undefined,
    },
    {
      title: 'Shared with You',
      id: TAB_SHARED_WITH_YOU,
      disabled: sharedWithYouDisabled,
      count: listsSource.sharedWithYou.length,
      tooltipProps: sharedWithYouDisabled
        ? {
            tooltipTitle:
              'Lists that teammates have shared with you will appear here',
          }
        : undefined,
    },
  ];
  if (partnerId) return baseTabs;
  const foldersDisabled = !folders.value.length;
  return [
    ...baseTabs,
    {
      title: 'Folders',
      id: TAB_FOLDERS,
      disabled: foldersDisabled,
      count: folders.value.length,
      tooltipProps:
        foldersDisabled && !isFreeTier.value
          ? { tooltipTitle: 'Folders you create will appear here' }
          : undefined,
    },
  ];
});

const activeSegmentTitle = computed(
  () => tabs.value.find((tab) => tab.id === activeTab.value)?.title,
);
const combinedLists = computed(() => {
  switch (activeTab.value) {
    case TAB_ALL:
      return combinedListsByOwner.value.yourLists.concat(
        combinedListsByOwner.value.sharedWithYou,
      );
    case TAB_YOUR_LISTS:
      return combinedListsByOwner.value.yourLists;
    case TAB_SHARED_WITH_YOU:
      return combinedListsByOwner.value.sharedWithYou;
    default:
      return [];
  }
});

const currentLists = computed(() => {
  if (isOnListsPage.value) {
    return combinedLists.value;
  }
  return reportsByOwner.value.yourReports
    .concat(reportsByOwner.value.sharedWithYou)
    .filter((report) => report.folder_uuid === folderId);
});

function getIsDisabledByTier(
  listOrFolder: SharedList | TrimmedReport | ReportFolder,
): boolean {
  if (!isFreeTier.value) return false;
  if (isOnboardingDemoReport(listOrFolder)) {
    return false;
  }
  if ('permissions' in listOrFolder) {
    const creatorOrg = listOrFolder.permissions?.find(
      (org) => org.org_access === CREATOR,
    );
    const isExternallyShared =
      creatorOrg != null && creatorOrg.organization_id !== currentOrg.value?.id;
    if (isExternallyShared) return false;
    return hasPartialAccess.value ? !listOrFolder['is_enabled?'] : true;
  }
  if ('user_access' in listOrFolder) {
    return listOrFolder.shared_partner_org_id !== currentOrg.value?.id;
  }
  return true;
}

function enrichReportCell(
  listOrFolder: SharedList | TrimmedReport | ReportFolder,
): ReportCell {
  return {
    isOnReportsPage: isOnListsPage.value,
    hasWritePermissions,
    trialOver,
    hideExport: Boolean(hideExport.value),
    isDisabledByTier: getIsDisabledByTier(listOrFolder),
    ...getCellInfo(listOrFolder),
  };
}

function getFoldersForActiveTab(): ReportFolder[] {
  return activeTab.value === TAB_FOLDERS ? folders.value : [];
}

const listsForTable = computed<ReportCell[]>(() => {
  const includeFolders = isOnListsPage.value && !partnerId;
  const baseLists = [
    ...(includeFolders ? getFoldersForActiveTab() : []),
    ...currentLists.value,
  ];

  const enrichedItems = baseLists
    .map(enrichReportCell)
    .filter(
      (item) =>
        isOnboardingDemoEnabled.value || !isOnboardingDemoReport(item.report),
    );

  const sortedItems = isFreeTier.value
    ? enrichedItems.toSorted((a, b) => {
        if (a.isDisabledByTier !== b.isDisabledByTier) {
          return a.isDisabledByTier ? 1 : -1;
        }
        const aUpdated = a.report?.updated_at ?? '';
        const bUpdated = b.report?.updated_at ?? '';
        if (aUpdated === bUpdated) return 0;
        return aUpdated > bUpdated ? -1 : 1;
      })
    : enrichedItems;

  if (!partnerId) return sortedItems;

  return sortedItems.filter((item) => {
    if (item.isFolder) return true;
    return item.partnersInReport?.some((partner) => partner.id === partnerId);
  });
});

const filterValueOptions = computed<Record<string, FilterProp[]>>(() => {
  const ownersById = listsForTable.value.reduce((acc, item) => {
    if (item.isFolder) return acc;
    const user = item.createdByUser;
    if (!user?.id || acc.has(user.id)) return acc;
    const { first_name, last_name, email } = user;
    const label =
      first_name || last_name
        ? [first_name, last_name].filter(Boolean).join(' ')
        : email;
    acc.set(user.id, label);
    return acc;
  }, new Map<number, string>());
  return {
    partners_in_list: partnerOrgs.value.map((org) => ({
      label: org.name,
      value: org.id,
    })),
    owner: Array.from(ownersById, ([value, label]) => ({ label, value })),
  };
});

function getSearchableText(item: ReportCell): string {
  const parts: string[] = [];

  if (item.report?.name) {
    parts.push(item.report.name.toLowerCase());
  }

  if (item.partnersInReport?.length) {
    for (const partner of item.partnersInReport) {
      if (partner.name) {
        parts.push(partner.name.toLowerCase());
      }
    }
  }

  return parts.join(' ');
}

const rows = computed<ReportCell[]>(() => {
  const query = debouncedSearchQuery.value.toLowerCase().trim();

  return listsForTable.value.filter((item) => {
    if (item.isFolder) {
      // For folders, only apply name search
      if (query && item.report?.name) {
        return item.report.name.toLowerCase().includes(query);
      }
      return true;
    }

    if (query) {
      const isAllPartnersReport =
        !item.isStaticList &&
        item.report?.consolidated_report_type === 'ecosystem';

      if (isAllPartnersReport) {
        const matchesAllPartnersText = 'all partners'.includes(query);
        if (!matchesAllPartnersText && !searchMatchesAnyPartner(query)) {
          if (!item.report?.name?.toLowerCase().includes(query)) {
            return false;
          }
        }
      } else {
        const searchableText = getSearchableText(item);
        if (!searchableText.includes(query)) {
          return false;
        }
      }
    }

    // Partner filter
    if (selectedPartnerIds.value.length) {
      const hasMatchingPartner = item.partnersInReport.some((partner) =>
        selectedPartnerIds.value.includes(partner.id),
      );
      if (!hasMatchingPartner) return false;
    }

    // Owner filter
    if (selectedOwnerIds.value.length) {
      if (!selectedOwnerIds.value.includes(item.createdByUser?.id)) {
        return false;
      }
    }

    return true;
  });
});

defineExpose({ clearSelection });
</script>

<style lang="pcss" scoped>
@reference "../../css/theme.css";
:deep(.ag-checkbox-input-wrapper.ag-disabled) {
  @apply invisible;
}
:deep(.ag-cell-wrapper) {
  @apply h-full;
}
:deep(.ag-cell.ag-column-first) {
  @apply pr-0 mr-0;
}
:deep(.ag-cell:not(.ag-column-first)) {
  @apply pl-0;
}

:deep(.bitts-quick-filters) > div {
  @apply w-full;
}

:deep(.bitts-quick-filter) {
  @apply mt-12;
}

:deep(.bitts-quick-filters__filters .c-bitts-btn:not(.c-bitts-btn-icon)) {
  @apply mt-12;
}

:deep(.bitts-quick-filter-select) {
  @apply ml-auto;
}

:deep(.ag-overlay-no-rows-wrapper .c-bitts-empty-state-large-border) {
  @apply border-none;
}

.lists-table__controls {
  @apply flex items-center gap-8;
}

:deep(.ag-row-odd),
:deep(.ag-row-even) {
  @apply rounded-8;
}

:deep(.ag-row) {
  @apply cursor-pointer;

  &:hover:not(:has(.cursor-not-allowed)) {
    @apply bg-neutral-background;
  }
}

:deep(.ag-row:has(.cursor-not-allowed)) {
  @apply cursor-not-allowed;
}

:deep(.ag-row-selected::before) {
  @apply rounded-8;
}

:deep(.ag-body-viewport) {
  @apply px-16 pt-16;
  padding-bottom: var(--locked-rows-extra-padding, 16px);
}

:deep(.ag-header-viewport) {
  @apply px-16;
}

:deep(.ag-header-cell::before) {
  @apply hidden;
}

:deep(.bitts-table-pro .ag-header-cell-text) {
  @apply text-neutral-text-strong font-extrabold;
}

:deep(.fa-home) {
  @apply hidden;
}
</style>
